import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import Navbar from "../components/Navbar";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

const VERDICTS = {
  publish_now:    { color: "#059669", bg: "#d1fae5" },
  minor_revision: { color: "#0284c7", bg: "#e0f2fe" },
  major_revision: { color: "#d97706", bg: "#fef3c7" },
  structural_edit:{ color: "#ea580c", bg: "#ffedd5" },
  rewrite:        { color: "#dc2626", bg: "#fee2e2" },
  reject:         { color: "#7f1d1d", bg: "#fecaca" },
};

export default function Dashboard() {
  const { user, subscription, getToken } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const L = lang.dashboard;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/reports?limit=5&sort=date`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const reportsRemaining = subscription
    ? subscription.type === "per_report"
      ? subscription.reports_total - subscription.reports_used
      : "∞"
    : 0;

  const expiresAt = subscription?.expires_at
    ? new Date(subscription.expires_at).toLocaleDateString("ro-RO")
    : null;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <Navbar />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "24px 16px" }}>

        {/* Welcome */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 4px", fontFamily: "Georgia, serif" }}>
            {L.welcome(user?.name || user?.email?.split("@")[0])}
          </h1>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0, fontFamily: "sans-serif" }}>
            {lang.common.footerNote}
          </p>
        </div>

        {/* KPI cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
          {[
            { label: L.kpiReports,   value: reports.length },
            { label: L.kpiRemaining, value: reportsRemaining },
            { label: L.kpiPlan,      value: subscription ? subscription.tier.replace("_", " ") : L.noPlan },
            { label: L.kpiExpires,   value: expiresAt || "–" },
          ].map(kpi => (
            <div key={kpi.label} style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
              <p style={{ fontSize: 11, color: "#9ca3af", margin: "0 0 4px", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: 0.5 }}>{kpi.label}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: "#1d4ed8", margin: 0, fontFamily: "sans-serif" }}>{kpi.value}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ display: "flex", gap: 10, marginBottom: 28 }}>
          <button onClick={() => navigate("/screening")}
            style={{ padding: "10px 20px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "sans-serif" }}>
            {L.btnNewReport}
          </button>
          <button onClick={() => navigate("/rapoarte")}
            style={{ padding: "10px 20px", background: "#fff", color: "#374151", border: "0.5px solid #e5e7eb", borderRadius: 8, fontWeight: 500, fontSize: 14, cursor: "pointer", fontFamily: "sans-serif" }}>
            {L.btnViewAll}
          </button>
        </div>

        {/* Recent reports */}
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 14px", fontFamily: "sans-serif" }}>{L.recentTitle}</h2>

          {loading && (
            <p style={{ fontSize: 13, color: "#9ca3af", fontFamily: "sans-serif" }}>{lang.common.loading}</p>
          )}

          {!loading && reports.length === 0 && (
            <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "32px 20px", textAlign: "center" }}>
              <p style={{ fontSize: 15, fontWeight: 500, margin: "0 0 6px", fontFamily: "sans-serif" }}>{L.noReports}</p>
              <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 16px", fontFamily: "sans-serif" }}>{L.noReportsDesc}</p>
              <button onClick={() => navigate("/screening")}
                style={{ padding: "9px 20px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "sans-serif" }}>
                {L.btnStart}
              </button>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {reports.map(r => {
              const v = VERDICTS[r.verdict] || {};
              return (
                <div key={r.id} onClick={() => navigate("/rapoarte")}
                  style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 16 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#93c5fd"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#e5e7eb"}>

                  <div style={{ width: 48, textAlign: "center", flexShrink: 0 }}>
                    <p style={{ fontSize: 20, fontWeight: 700, color: "#1d4ed8", margin: 0 }}>{Math.round(r.feur_score)}</p>
                    <p style={{ fontSize: 10, color: "#9ca3af", margin: 0, fontFamily: "sans-serif" }}>FEUR</p>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <span style={{ fontWeight: 600, fontSize: 14, color: "#111827", fontFamily: "sans-serif" }}>{r.title}</span>
                      {r.verdict && (
                        <span style={{ background: v.bg, color: v.color, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, fontFamily: "sans-serif" }}>
                          {lang.verdicts[r.verdict]}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "#6b7280", margin: 0, fontFamily: "sans-serif" }}>
                      {r.author || lang.common.unknown} · {r.genre_label} · {new Date(r.created_at).toLocaleDateString("ro-RO")}
                    </p>
                  </div>

                  <div style={{ fontSize: 16, color: "#d1d5db" }}>›</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
